import {
  Box,
  Button,
  Heading,
  HStack,
  Text,
  VStack,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react'
import { Form, Formik } from 'formik'
import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import { BaseLayout } from '..'
import { colors } from '../../../ui-kit'
import InvoicesTable from '../../components/Billing/InvoicesTable'
import MoneyField from '../../components/Form/MoneyField'
import { UserContext } from '../../providers'
import { MainContentHeadingBox, MainContentWrapper } from '../Base/MainContent'
import useToast from '../../hooks/useToast'
import PermissionGuard from "../../components/PermissionGuard"
import { PermissionsEnum } from '../../../utils'
import { DiscountedPricingAlertBox } from '../../components/Billing/DiscountedPricing'

const BillingAlertsLayout = (): React.ReactElement => {
  const { t } = useTranslation()
  const toast = useToast()
  const { user } = useContext(UserContext)

  const { request, loading } = useFetch({
    cachePolicy: CachePolicies.NO_CACHE,
  })

  const [billingAlerts, updateBillingAlerts] = useState<{
    thresholdAmount: number | null
  } | null>(null)

  const billingAlertNotSet =
    billingAlerts?.thresholdAmount === null ||
    billingAlerts?.thresholdAmount === undefined

  useEffect(() => {
    if (billingAlerts === null) {
      request
        .get(`/api/billing/alerts/${user?.clientNumber}`)
        .then(data => {
          updateBillingAlerts(data)
        })
        .catch(err => {
          console.log(err)
        })
    }
  }, [])

  const onSubmit = async (values: {
    thresholdAmount: number
  }): Promise<void> => {
    try {
      await request.post(`/api/billing/alerts/${user?.clientNumber}`, values)
      if (request.error) {
        throw new Error("Can't set billing alert")
      }
      toast({
        description: t(
          'billing_update_success',
          'Billing alert has been updated successfully',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        variant: 'outline',
        position: 'top-right',
      })
      updateBillingAlerts({
        ...billingAlerts,
        thresholdAmount: values.thresholdAmount,
      })
    } catch (error) {
      toast({
        description: t('something_went_wrong', 'Something went wrong'),
        status: 'error',
        isClosable: true,
        duration: 5000,
        variant: 'outline',
        position: 'top-right',
      })
    }
  }

  const removeBillingAlerts = async (): Promise<void> => {
    try {
      await request.delete(`/api/billing/alerts/${user?.clientNumber}`)
      if (request.error) {
        throw new Error("Can't remove billing alert")
      }
      updateBillingAlerts({
        ...billingAlerts,
        thresholdAmount: null,
      })
      toast({
        description: t(
          'billing_update_success',
          'Billing alert has been removed successfully',
        ),
        status: 'success',
        isClosable: true,
        duration: 5000,
        variant: 'outline',
        position: 'top-right',
      })
    } catch (error) {
      toast({
        description: t('something_went_wrong', 'Something went wrong'),
        status: 'error',
        isClosable: true,
        duration: 5000,
        variant: 'outline',
        position: 'top-right',
      })
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={{
        thresholdAmount:
          billingAlerts?.thresholdAmount ?? ('' as unknown as number),
      }}
      onSubmit={onSubmit}
      validationSchema={Yup.object().shape({
        thresholdAmount: Yup.number()
          .positive('Must be greater than 0')
          .required('Required'),
      })}
      validateOnMount={true}
      validateOnBlur={true}
      validateOnChange={true}
      isInitialValid={true}
    >
      {({ values, isValid, dirty }) => (
        <Form>
          <BaseLayout>
            <MainContentHeadingBox>
              <Heading as="h5" size="md" fontWeight="600">
                {' '}
              </Heading>
              <PermissionGuard permission={PermissionsEnum.SELF_BILLING_MANAGE} >
                {
                  (hasBillingWritePermission: boolean) => (
                    hasBillingWritePermission ? (
                      <HStack spacing="1em">
                        <Button
                          variant="deleteCTA"
                          onClick={removeBillingAlerts}
                          disabled={billingAlertNotSet}
                        >
                          {t('remove_billing_alert_button', 'Remove alert')}
                        </Button>
                        <Button
                          type="submit"
                          variantcolor="green"
                          isLoading={loading}
                          disabled={
                            loading ||
                            !isValid ||
                            !dirty ||
                            billingAlerts?.thresholdAmount ===
                              values.thresholdAmount
                          }
                        >
                          {t('save', 'Save')}
                        </Button>
                      </HStack>
                    ) : <></>
                  )
                }
              </PermissionGuard>
            </MainContentHeadingBox>
            <MainContentWrapper style={{ gap: '0.5em' }}>
              <VStack
                px={8}
                py={6}
                width="full"
                justifyContent="flex-start"
                alignItems="flex-start"
                spacing="1em"
              >
                <DiscountedPricingAlertBox />
                <Box>
                  <Text color={colors.appSlate}>
                    <b>{t('billing_alerts', 'Billing Alerts')}</b>
                    <br />
                    {t(
                      'billing_alerts_description',
                      'Set billing alerts to receive a notification if your total upcoming bill exceeds the specified threshold.',
                    )}
                  </Text>
                </Box>
                {billingAlertNotSet && !loading ? (
                  <Box>
                    <Alert status="info">
                      <AlertIcon height="1em" width="1em" />
                      <AlertDescription fontSize="sm">
                        <Text lineHeight="1.5">
                          {t('no_billing_alert_set', 'No billing alert set')}
                        </Text>
                      </AlertDescription>
                    </Alert>
                  </Box>
                ) : null}
                <Box>
                  <PermissionGuard permission={PermissionsEnum.SELF_BILLING_MANAGE} >
                    {
                      (hasBillingWritePermission: boolean) => (
                        <MoneyField
                          name="thresholdAmount"
                          label={t(
                            'billing_alerts_threshold_amount',
                            'Threshold amount',
                          )}
                          required={true}
                          currency={user?.pricingPlan.currency ?? 'USD'}
                          placeholder={loading ? '0.00' : t(
                            'billing_alerts_threshold_total_amount',
                            'Enter total amount',
                          )}
                          disabled={!hasBillingWritePermission || loading}
                          loading={loading}
                        />
                      )
                    }
                  </PermissionGuard>
                </Box>
                <Box width="full">
                  <br />
                  <Text color={colors.appSlate}>
                    <b>{t('recent_invoices', 'Recent Invoices')}</b>
                  </Text>
                  <br />
                  <InvoicesTable minimal />
                </Box>
              </VStack>
            </MainContentWrapper>
          </BaseLayout>
        </Form>
      )}
    </Formik>
  )
}

export default BillingAlertsLayout
